/**
 * The page grid — the designer's brief part 3 §B (`docs/adr/9038`).
 *
 * A data page widens with the screen, left-aligned after the rail, on 12 columns; it is never
 * centred and stops at `page.max-width` (1760px), still left-aligned. The measures step with the
 * screen through tokens (`tokens/layout.json`: `page.margin`, `grid.gutter`, `panel.width`,
 * `list.width`, `plot.height`, …), so nothing here restates a breakpoint.
 *
 *   `PAGE_CONTENT`   the page's content box: rail + margin, the max width, `data-layout-content`
 *   `LayoutRow`      one band of blocks on the 12 columns — `full` 12, `pair` 6 + 6, `wide` 8 + 4,
 *                    `tiles` n equal columns — equal heights by `align-items: stretch`
 *   `SeeAllRows`     «Vedi tutte le N →» under a table cut to fit beside a chart; N is a real count
 *
 * The attributes are the contract `e2e/layout-audit.spec.ts` measures (§B6): `data-layout-content`
 * on the page's content box, `data-layout-row` on a band, `data-layout-block` on each of its
 * blocks (and on `Panel`, `Chart`, `DataTable`, `KpiBand`, `KpiRow`), `data-layout-plot` on a
 * chart's plot area.
 */

import { Children, createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FOCUS_RING } from '../primitives';

/**
 * The page's content box (§B1): its inline start is the rail plus `page.margin` at every width,
 * its end keeps the same margin, and beyond `page.max-width` it stops, left-aligned. A MARGIN
 * rather than a padding, so the box itself starts where the content does — the audit measures
 * the box's own edge. Carries `data-layout-content` wherever it is used.
 */
export const PAGE_CONTENT_BOX = 'mx-page min-w-0 max-w-content';

/** The routed page's body: the content box, its blocks stacked 24px apart. */
export const PAGE_CONTENT = `${PAGE_CONTENT_BOX} flex flex-col gap-6 py-6`;

/** The shape of a band (§B2, §B4). Anything else is a caller's mistake, not a layout. */
export type LayoutRowKind =
  /** One block on the 12 columns: the sentence, the KPIs, the main chart, the table. */
  | 'full'
  /** Two blocks, 6 + 6: two readings of the same thing, or a chart beside the table that details it. */
  | 'pair'
  /** 8 + 4: a main block and its short companion — the Cockpit's «Da decidere» and «Copertura dati». */
  | 'wide'
  /** n equal blocks (2, 3 or 4): small multiples on one scale, or the Cockpit's app tiles. */
  | 'tiles';

/** The column span each block takes from `lg` up, as whole class names so Tailwind emits them. */
const SPAN_CLASS: Readonly<Record<number, string>> = {
  12: 'lg:col-span-12',
  8: 'lg:col-span-8',
  6: 'lg:col-span-6',
  4: 'lg:col-span-4',
  3: 'lg:col-span-3',
};

/**
 * The spans of a band, or a throw. §B2 R: «Mai tre blocchi in una riga» — three different blocks
 * in a row is refused; three or four EQUAL blocks are `tiles` (small multiples on one scale, §B4
 * «4 × 3 o 3 × 4», or the Cockpit's apps). Pure.
 */
export function layoutSpans(kind: LayoutRowKind, count: number): readonly number[] {
  if (kind === 'full') {
    if (count !== 1) throw new Error(`a 'full' layout row holds one block; got ${count}`);
    return [12];
  }
  if (kind === 'pair' || kind === 'wide') {
    if (count !== 2) throw new Error(`a '${kind}' layout row holds two blocks; got ${count}`);
    return kind === 'pair' ? [6, 6] : [8, 4];
  }
  if (count < 2 || count > 4) throw new Error(`a 'tiles' layout row holds two to four equal blocks; got ${count}`);
  return Array.from({ length: count }, () => 12 / count);
}

/**
 * §B4: «se un blocco sarebbe più alto dell'altro di oltre 1,5 volte, non si affiancano». Given the
 * two blocks' own heights side by side, whether they stack. An unmeasured height (0: no layout
 * yet, or jsdom) never stacks. Pure.
 */
export function shouldStack(heights: readonly number[], ratio = 1.5): boolean {
  if (heights.length !== 2) return false;
  const [a = 0, b = 0] = heights;
  if (a <= 0 || b <= 0) return false;
  return Math.max(a, b) / Math.min(a, b) > ratio;
}

/** Inside a side-by-side band: a chart there takes `plot.height` and a two-line title floor. */
const SideBySide = createContext(false);

/** Whether the caller sits in a band of two or more blocks side by side (§B4). */
export function useSideBySide(): boolean {
  return useContext(SideBySide);
}

/** Inside layout S's visual band: a plot stands no taller than `plot.max-height` (320px, §B2 S). */
const VisualCapContext = createContext(false);

/** Whether a plot sits in layout S's visual band, where it is capped at 320px. */
export function useVisualCap(): boolean {
  return useContext(VisualCapContext);
}

/** Layout S's visual band: the plot inside widens with the page and never grows past 320px (§B2). */
export function VisualCap({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <VisualCapContext.Provider value>{children}</VisualCapContext.Provider>;
}

/** The block title's floor in a side-by-side band: two lines, so the plots start at one height (§B4). */
export const BLOCK_TITLE_FLOOR = 'min-h-[52px]';

export interface LayoutRowProps {
  /** The band's name, for the audit's messages («larghezza usata in {id}»). */
  readonly id: string;
  readonly kind: LayoutRowKind;
  /**
   * `pair` only: stack the two blocks, each on 12 columns, when one would stand more than 1.5
   * times taller than the other (§B4). On by default for a pair; `wide` is two blocks the page
   * holds level on purpose (the Cockpit's), and `tiles` are equal by construction.
   */
  readonly balance?: boolean;
  readonly children: ReactNode;
}

/**
 * One band on the 12 columns (§B2). Each child is wrapped in a block cell that is the grid item —
 * `align-items: stretch` makes the cells of a band equally tall, so a shorter table's frame runs
 * down to the same base as its neighbour (§B5 Cockpit). Below `lg` every block is full width.
 *
 * A balanced pair whose blocks differ by more than 1.5× is drawn as two bands of one block each,
 * one under the other on the 12 columns (§B4) — two bands, not one band of two, because blocks
 * of one band are side by side by definition (the audit holds them to one height).
 */
export function LayoutRow({ id, kind, balance = kind === 'pair', children }: LayoutRowProps): React.JSX.Element {
  const items = Children.toArray(children);
  const spans = layoutSpans(kind, items.length);
  const natural = useRef<(HTMLDivElement | null)[]>([]);
  const [stacked, setStacked] = useState(false);
  // The width the decision was taken at: a stacked pair is re-measured side by side only when the
  // band's width changes, so the two layouts do not chase each other.
  const decidedAt = useRef<number | null>(null);
  const bandRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!balance || typeof ResizeObserver === 'undefined') return undefined;
    const band = bandRef.current;
    if (band === null) return undefined;
    const measure = (): void => {
      const width = band.getBoundingClientRect().width;
      if (stacked) {
        if (decidedAt.current !== null && Math.abs(width - decidedAt.current) > 1) {
          decidedAt.current = null;
          setStacked(false);
        }
        return;
      }
      const heights = natural.current.map((cell) => cell?.getBoundingClientRect().height ?? 0);
      if (shouldStack(heights)) {
        decidedAt.current = width;
        setStacked(true);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(band);
    for (const cell of natural.current) if (cell !== null) observer.observe(cell);
    return () => observer.disconnect();
  }, [balance, stacked, items.length]);

  // The block's own height, unstretched — what §B4's 1.5 rule compares. A band that does not
  // balance lets its content fill the stretched cell instead (the Cockpit's).
  const content = (child: ReactNode, index: number): React.JSX.Element => (
    <div ref={(element) => { natural.current[index] = element; }} className={`flex min-w-0 flex-col ${balance ? '' : 'grow'}`}>
      {child}
    </div>
  );

  return (
    <SideBySide.Provider value={spans.length > 1 && !stacked}>
      <div ref={bandRef} data-layout-kind={kind} className="gap-gutter flex min-w-0 flex-col">
        {stacked ? items.map((child, index) => (
          <div
            // A band's blocks are positions, fixed by its kind: the index is their identity.
            key={index}
            data-layout-row={`${id}-${index + 1}`}
            data-layout-stacked=""
            className="min-w-0"
          >
            <div data-layout-block="" className="flex min-w-0 flex-col">{content(child, index)}</div>
          </div>
        )) : (
          <div data-layout-row={id} className="gap-gutter grid min-w-0 grid-cols-1 items-stretch lg:grid-cols-12">
            {items.map((child, index) => (
              <div
                key={index}
                data-layout-block=""
                className={`flex min-w-0 flex-col ${SPAN_CLASS[spans[index] ?? 12] ?? ''}`}
              >
                {content(child, index)}
              </div>
            ))}
          </div>
        )}
      </div>
    </SideBySide.Provider>
  );
}

/**
 * «Vedi tutte le N →» (§B4): a table beside a chart shows the rows that fit, then this. `total` is
 * the count of the rows the table stands for — the read's own length, never an estimate — and the
 * link is drawn only when rows were left out. Renders nothing otherwise.
 */
export function SeeAllRows({ shown, total, href, onOpen }: {
  readonly shown: number;
  readonly total: number;
  /** Where every row is — a page, or the table's own full view. */
  readonly href: string;
  readonly onOpen?: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!(total > shown)) return null;
  return (
    <a
      href={href}
      data-see-all=""
      onClick={onOpen === undefined ? undefined : (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        onOpen();
      }}
      className={`text-ink-primary self-start pt-2 text-[13px] font-semibold underline underline-offset-2 ${FOCUS_RING}`}
    >
      <span>{t('layout.seeAll', { count: total })}</span>
      <span aria-hidden className="ms-1">→</span>
    </a>
  );
}

// ── Layout T · text ──────────────────────────────────────────────────────────

/** Below this CONTENT width the index goes to the top, closed (§B3). */
export const TEXT_INDEX_INLINE_BELOW = 1100;

export interface TextSection {
  /** The H2's id — the index's anchor. */
  readonly id: string;
  /** The H2, already in words. */
  readonly title: string;
  readonly children: ReactNode;
}

export interface TextRelated {
  readonly href: string;
  readonly label: string;
  readonly onOpen?: () => void;
}

export interface TextTemplateProps {
  /** Before the first H2, at the reading measure. */
  readonly intro?: ReactNode;
  readonly sections: readonly TextSection[];
  /**
   * «COLLEGATE»: the app's pages the text cites, as links. The caller derives them from what the
   * text actually links to; none, and the heading is not drawn.
   */
  readonly related: readonly TextRelated[];
}

/**
 * The section the reader is in: the first heading in the top part of the viewport, else the last
 * one scrolled past. `IntersectionObserver` where the browser has one; without it (jsdom, print)
 * the first section. Returns the section id.
 */
function useCurrentSection(ids: readonly string[]): string | null {
  const [current, setCurrent] = useState<string | null>(ids[0] ?? null);
  const key = ids.join('|');
  useEffect(() => {
    const Observer = (globalThis as { IntersectionObserver?: typeof IntersectionObserver }).IntersectionObserver;
    if (Observer === undefined) return undefined;
    const visible = new Map<string, boolean>();
    const headings = ids
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);
    const observer = new Observer((entries) => {
      for (const entry of entries) visible.set(entry.target.id, entry.isIntersecting);
      const inView = ids.find((id) => visible.get(id) === true);
      if (inView !== undefined) { setCurrent(inView); return; }
      // Nothing in the band: the last heading above the viewport is the section being read.
      const above = headings.filter((heading) => heading.getBoundingClientRect().top < 0);
      const last = above[above.length - 1];
      if (last !== undefined) setCurrent(last.id);
    }, { rootMargin: '0px 0px -60% 0px' });
    for (const heading of headings) observer.observe(heading);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for `ids`: a new array with the same ids is the same headings
  }, [key]);
  return current;
}

/**
 * The scroll offset that brings an entry into a scrolled box with `margin` to spare, or `null` when
 * it is already in view — the least movement, so the index does not jump while the reader stays
 * inside one screenful of it. Offsets are relative to the box's content. Pure, exported for tests.
 */
export function revealOffset(
  box: { readonly scrollTop: number; readonly height: number },
  entry: { readonly top: number; readonly height: number },
  margin = 24,
): number | null {
  if (entry.top - margin < box.scrollTop) return Math.max(0, entry.top - margin);
  if (entry.top + entry.height + margin > box.scrollTop + box.height) return entry.top + entry.height + margin - box.height;
  return null;
}

/**
 * Keeps the index's current entry (`aria-current`) in view inside the index's own scroll, as the
 * reader moves through the text (issue 249). It scrolls the index only — never the page, which is
 * where the reader is.
 */
function useRevealCurrent(current: string | null): React.RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const box = ref.current;
    if (box === null || current === null) return;
    const entry = box.querySelector<HTMLElement>('[aria-current]');
    if (entry === null) return;
    const boxRect = box.getBoundingClientRect();
    const entryRect = entry.getBoundingClientRect();
    const next = revealOffset(
      { scrollTop: box.scrollTop, height: box.clientHeight },
      { top: entryRect.top - boxRect.top + box.scrollTop, height: entryRect.height },
    );
    if (next !== null) box.scrollTop = next;
  }, [current]);
  return ref;
}

/** The template's own width, for §B3's «sotto i 1100px di contenuto». `null` until measured. */
function useOwnWidth(): [React.RefObject<HTMLDivElement | null>, number | null] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === 'undefined') return undefined;
    const read = (): void => {
      const next = element.getBoundingClientRect().width;
      // No layout (jsdom) measures 0: keep the wide reading rather than claim a narrow page.
      if (next > 0) setWidth(next);
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

function IndexList({ sections, current, related }: {
  readonly sections: readonly TextSection[];
  readonly current: string | null;
  readonly related: readonly TextRelated[];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <ul className="flex flex-col">
        {sections.map((section) => {
          const here = section.id === current;
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                {...(here ? { 'aria-current': 'location' as const } : {})}
                onClick={(event) => {
                  const heading = document.getElementById(section.id);
                  if (heading === null) return;
                  event.preventDefault();
                  if (typeof heading.scrollIntoView === 'function') heading.scrollIntoView({ block: 'start' });
                  heading.focus({ preventScroll: true });
                }}
                // The current section: ink 700 and a 3px rule at the inline start in brand red —
                // an active navigation indicator (design/09 §5.2 rule 1), drawn by
                // `.bata-toc-current` in src/styles/index.css (ADR 9038).
                className={[
                  'block py-[5px] ps-3 text-[13px] leading-[18px]',
                  here ? 'bata-toc-current text-ink-primary font-bold' : 'text-ink-label hover:text-ink-primary',
                  FOCUS_RING,
                ].join(' ')}
              >
                {section.title}
              </a>
            </li>
          );
        })}
      </ul>
      {related.length > 0 && (
        <>
          <p className="text-ink-secondary pt-4 text-[9px] font-bold uppercase leading-4 tracking-[0.1em]">{t('layout.text.related')}</p>
          <ul className="flex flex-col gap-1 pt-1">
            {related.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={link.onOpen === undefined ? undefined : (event) => {
                    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                    event.preventDefault();
                    link.onOpen?.();
                  }}
                  className={`text-ink-primary text-[13px] underline underline-offset-[3px] ${FOCUS_RING}`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/**
 * Layout T (§B2, §B3): the text on columns 1–7 at a fixed measure (16px / 1.6, at most 68ch),
 * column 8 empty, the index on 9–12 — «IN QUESTA PAGINA», the H2s with the one being read marked,
 * then «COLLEGATE». The index is sticky 24px from the top. Below 1100px of content it goes to the
 * top, closed («In questa pagina ⌄»).
 *
 * Not built: §B3's break-out of a table, figure or code block to the 12 columns (the index
 * scrolling away and coming back). No text page has one today — Documentation is prose, lists and
 * an accordion — and a mechanism with no content to carry would be untested (ADR 9038).
 */
export function TextTemplate({ intro, sections, related }: TextTemplateProps): React.JSX.Element {
  const { t } = useTranslation();
  const ids = sections.map((section) => section.id);
  const current = useCurrentSection(ids);
  const [ref, width] = useOwnWidth();
  const inline = width !== null && width < TEXT_INDEX_INLINE_BELOW;
  const indexRef = useRevealCurrent(current);
  return (
    <div
      ref={ref}
      data-layout="text"
      className={inline ? 'flex flex-col gap-6' : 'gap-gutter grid grid-cols-12'}
    >
      {inline && (
        // On top of the text, the index takes the text's measure too: full width, its «Collegate»
        // line ran 976px at 1280, past the 740px the layout audit holds every paragraph to.
        <details data-layout-toc="" className="max-w-measure border-ink-primary border-t-2 py-2">
          <summary className={`text-ink-primary flex cursor-pointer list-none items-center gap-2 text-[13px] font-semibold ${FOCUS_RING}`}>
            <span>{t('layout.text.onThisPage')}</span>
            <span aria-hidden>⌄</span>
          </summary>
          <nav aria-label={t('layout.text.onThisPage')} className="pt-2">
            <IndexList sections={sections} current={current} related={related} />
          </nav>
        </details>
      )}
      <article data-layout-text="" className={inline ? 'flex flex-col gap-8' : 'row-start-1 flex min-w-0 flex-col gap-8 [grid-column:1/8]'}>
        {intro !== undefined && <div className="max-w-measure text-[16px] leading-[1.6]">{intro}</div>}
        {sections.map((section) => (
          <section key={section.id} aria-labelledby={section.id} className="flex flex-col gap-3">
            <h2
              id={section.id}
              tabIndex={-1}
              className={`font-display text-ink-primary scroll-mt-6 text-[22px] font-bold uppercase leading-[1.2] ${FOCUS_RING}`}
            >
              {section.title}
            </h2>
            <div className="max-w-measure flex flex-col gap-3 text-[16px] leading-[1.6]">{section.children}</div>
          </section>
        ))}
      </article>
      {!inline && (
        <aside data-layout-toc="" className="row-start-1 [grid-column:9/13]">
          {/* Sticky AND bounded by the window, with its own scroll (issue 249): a 66-entry index
              was 1,711px in a 1,080px window and scrolled away with the page, the marked section
              with it. The current entry is kept in view inside it (`useRevealCurrent`). */}
          <nav
            ref={indexRef}
            aria-label={t('layout.text.onThisPage')}
            data-layout-toc-scroll=""
            className="sticky top-6 flex max-h-[calc(100dvh-48px)] flex-col overflow-y-auto overscroll-contain"
          >
            <p className="text-ink-secondary pb-2 text-[9px] font-bold uppercase leading-4 tracking-[0.1em]">{t('layout.text.onThisPage')}</p>
            <IndexList sections={sections} current={current} related={related} />
          </nav>
        </aside>
      )}
    </div>
  );
}

